import React, { Dispatch, FC, JSX, SetStateAction, useEffect, useState } from 'react';
import { Button, Input, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { Spinner, useApi, useTranslation } from '@ibe/components';
import fallback from '../../Translations/generated/my-journeys.de.json';
import Keys from '../../Translations/generated/my-journeys.de.json.keys';
import { ApiBooking, ApiBookingLoginRequest } from '@ibe/api';
import * as yup from 'yup';
import useThgConfig from '../../Hooks/useThgConfig';

export const MY_JOURNEYS_MODAL_EVENT = 'thg-my-journey-modal';
export const TOKEN_LENGTH = 6;

type CustomEventPayload = { id: number };

const emailValidation = yup.string().email().required();

const MyJourneysModal: FC<{
  myJourneysUrl?: string;
  setBooking?: Dispatch<SetStateAction<ApiBooking | undefined>>;
  modalId?: number;
}> = ({ myJourneysUrl, setBooking, modalId }): JSX.Element => {
  const { t } = useTranslation('my-journeys', fallback);
  const api = useApi();
  const thgConfig = useThgConfig();

  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [bookingError, setBookingError] = useState(false);
  const [bookingNumber, setBookingNumber] = useState('');
  const [bookingNumberError, setBookingNumberError] = useState(false);
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailError, setEmailError] = useState(false);

  const [awaitingToken, setAwaitingToken] = useState(false);
  const [token, setToken] = useState('');
  const [tokenError, setTokenError] = useState(false);
  const [tokenTouched, setTokenTouched] = useState(false);

  const [resendLoading, setResendLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [tokenSubmitError, setTokenSubmitError] = useState(false);

  useEffect(() => {
    const handleEvent = (event: CustomEventInit<CustomEventPayload>) => {
      const eventModalId = event.detail?.id;
      const modal = document.getElementsByClassName('my-journeys__modal');
      if (
        modal.length === 0 &&
        ((!eventModalId && !modalId) || (eventModalId && modalId && eventModalId === modalId))
      ) {
        setModalOpen(true);
      }
    };

    window.addEventListener(MY_JOURNEYS_MODAL_EVENT, handleEvent);
    return () => window.removeEventListener(MY_JOURNEYS_MODAL_EVENT, handleEvent);
  }, []);

  const cleanUp = () => {
    setBookingNumber('');
    setLastName('');
    setEmailAddress('');
    setBookingError(false);
    setIsDirty(false);
    setAwaitingToken(false);
    setToken('');
    setTokenError(false);
    setTokenTouched(false);
    setTokenSubmitError(false);
    setResendLoading(false);
    setLoginLoading(false);
  };

  const handleRequestToken = async () => {
    const loginWithLastNameEnabled = thgConfig.loginWithLastNameEnabled && !lastName;
    if (!bookingNumber || loginWithLastNameEnabled) return;
    setIsLoading(true);
    setBookingError(false);
    setIsDirty(true);

    try {
      if (!bookingNumberError && !emailError && !lastNameError) {
        const bookingLoginRequest: ApiBookingLoginRequest = {
          bookingNumber: bookingNumber,
          emailAddress: emailAddress
        };
        thgConfig.loginWithLastNameEnabled && (bookingLoginRequest.lastName = lastName);

        await api.sendTokenForBooking(bookingLoginRequest);

        setAwaitingToken(true);
        setToken('');
        setTokenSubmitError(false);
        setTokenTouched(false);
      }
    } catch (err) {
      console.error('Unable to send token for booking:', err);
      setBookingError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendToken = async () => {
    setResendLoading(true);
    setTokenSubmitError(false);
    try {
      const bookingLoginRequest: ApiBookingLoginRequest = {
        bookingNumber: bookingNumber,
        emailAddress: emailAddress
      };
      thgConfig.loginWithLastNameEnabled && (bookingLoginRequest.lastName = lastName);
      await api.sendTokenForBooking(bookingLoginRequest);
    } catch (err) {
      console.error('Unable to resend token:', err);
      setBookingError(true);
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerifyTokenAndProceed = async () => {
    setTokenTouched(true);
    if (!token || token.trim().length !== TOKEN_LENGTH) {
      setTokenError(true);
      return;
    }

    setLoginLoading(true);
    setTokenSubmitError(false);
    try {
      const booking = await api.searchBookingByToken(token.trim(), bookingNumber);

      if (!booking) {
        setModalOpen(false);
        return;
      }

      if (setBooking) {
        setBooking(booking);
      } else if (myJourneysUrl) {
        const url = new URL(myJourneysUrl, window.location.origin);
        window.location.href = url.href;
      }

      setModalOpen(false);
    } catch (err) {
      console.error('Token verification failed:', err);
      setTokenSubmitError(true);
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <Modal
      isOpen={modalOpen}
      toggle={() => setModalOpen(v => !v)}
      className="my-journeys__modal"
      container={document.getElementById('iso') ?? document.body}
      aria-labelledby="myJourneysTitle"
      unmountOnClose
      onClosed={cleanUp}
    >
      <form
        onSubmit={async e => {
          e.preventDefault();
          if (!awaitingToken) {
            await handleRequestToken();
          } else {
            await handleVerifyTokenAndProceed();
          }
        }}
      >
        <ModalBody>
          <div className="my-journeys__modal__content">
            <h4 id="myJourneysTitle">
              {awaitingToken ? t(Keys.tokenHeadline) : t(Keys.modalHeadline)}
            </h4>
            <p>{awaitingToken ? t(Keys.tokenText) : t(Keys.modalText)}</p>
          </div>

          <button
            className="my-journeys__modal__close"
            type="button"
            onClick={() => setModalOpen(false)}
            aria-label={t(Keys.closeModal)}
          >
            <CloseIcon />
          </button>

          {!awaitingToken ? (
            <>
              <div className="form-input-container">
                <label htmlFor="bookingNumber" className="search-form__label">
                  {t(Keys.bookingNumber)}
                </label>
                <div className="position-relative">
                  <Input
                    id="bookingNumber"
                    className="search-form__input form-control"
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    value={bookingNumber}
                    onChange={e => {
                      const v = e.target.value;
                      setBookingNumber(v);
                      setBookingNumberError(!/^\d{4,16}$/.test(v));
                    }}
                  />
                  {bookingNumberError && isDirty && (
                    <div className="my-journeys__modal__error" role="alert">
                      {t(Keys.bookingNumberError)}
                    </div>
                  )}
                </div>
              </div>

              {thgConfig.loginWithLastNameEnabled ? (
                <div className="form-input-container">
                  <label htmlFor="lastName" className="search-form__label">
                    {t(Keys.lastName)}
                  </label>
                  <div className="position-relative">
                    <Input
                      id="lastName"
                      className="search-form__input form-control"
                      type="text"
                      autoComplete="family-name"
                      value={lastName}
                      onChange={e => {
                        const v = e.target.value;
                        setLastName(v);
                        setLastNameError(!/^[\p{L}\p{M}\-'\s]{2,60}$/u.test(v));
                      }}
                    />
                    {lastNameError && isDirty && (
                      <div className="my-journeys__modal__error" role="alert">
                        {t(Keys.lastNameError)}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <></>
              )}

              <div className="form-input-container">
                <label htmlFor="email" className="search-form__label">
                  {t(Keys.email)}
                </label>
                <div className="position-relative">
                  <Input
                    id="email"
                    className="search-form__input form-control"
                    type="email"
                    autoComplete="email"
                    value={emailAddress}
                    onChange={async e => {
                      const v = e.target.value;
                      setEmailAddress(v);
                      try {
                        await emailValidation.validate(v);
                        setEmailError(false);
                      } catch {
                        setEmailError(true);
                      }
                    }}
                  />
                  {emailError && isDirty && (
                    <div className="my-journeys__modal__error" role="alert">
                      {t(Keys.emailError)}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="form-input-container">
              <label htmlFor="token" className="search-form__label">
                {t(Keys.tokenLabel)}
              </label>
              <div className="position-relative">
                <Input
                  id="token"
                  className="search-form__input form-control"
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={TOKEN_LENGTH}
                  value={token}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '');
                    setToken(v);
                    setTokenSubmitError(false);
                    if (tokenTouched) {
                      setTokenError(v.trim().length !== TOKEN_LENGTH);
                    }
                  }}
                />
                {(tokenTouched && tokenError) || tokenSubmitError ? (
                  <div className="my-journeys__modal__error" role="alert" aria-live="assertive">
                    {tokenError ? t(Keys.tokenError) : t(Keys.tokenSubmitError)}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter className={awaitingToken ? 'token-step' : ''}>
          {!awaitingToken ? (
            <>
              {bookingError && (
                <div className="my-journeys__modal__error" role="alert">
                  {t(Keys.bookingError)}
                </div>
              )}
              <Button
                className="my-journeys__modal__submit"
                color="primary"
                disabled={
                  (!bookingNumber ||
                    (thgConfig.loginWithLastNameEnabled && !lastName) ||
                    lastNameError) ??
                  emailAddress ??
                  emailError ??
                  bookingNumberError ??
                  isLoading
                }
                type="submit"
                aria-label={t(Keys.myJourneyLoginModal)}
              >
                {t(Keys.search)}
                {isLoading && <Spinner />}
              </Button>
            </>
          ) : (
            <div className="button-group">
              <Button
                outline
                color="secondary"
                disabled={resendLoading}
                onClick={handleResendToken}
              >
                {t(Keys.resendPassword)}
                {resendLoading && <Spinner />}
              </Button>
              <Button
                className="my-journeys__modal__submit"
                color="primary"
                disabled={(token.trim().length !== TOKEN_LENGTH || tokenError) ?? loginLoading}
                type="submit"
              >
                {t(Keys.login)}
                {loginLoading && <Spinner />}
              </Button>
            </div>
          )}
        </ModalFooter>
      </form>
    </Modal>
  );
};

function CloseIcon(props: React.SVGProps<SVGSVGElement>) {
  /* eslint-disable-next-line */
  const [w, h, , , d] = faTimes.icon as [number, number, any, string, string | string[]];
  const path = Array.isArray(d) ? d.join(' ') : d;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} height="1em" aria-hidden="true" {...props}>
      <path d={path} fill="currentColor" />
    </svg>
  );
}

export default MyJourneysModal;
